/**
 * AssetLoadService.js
 * 
 * Created on 1-okt-2013, 14:43:40
 *
 * The AssetLoadService can load manifests of assets and groups of assets that are defined in an
 * asset configuration. Asset configuration is found in assets/assets.json. The first time something
 * is loaded with the service it will first load the configuration and then load the necessary data.
 * 
 * TODO: setup system to load and merge multiple asset configuration file
 * TODO: manage groups that are already loaded and do not load them again
 * TODO: unload groups and functionality
 * 
 * @author Ivo Swartjes for Ranj
 **/

import Queue from "../../../../lib/html5-software-library/src/ranj/util/datastructures/queue/Queue";
import { isArray, isNull, isString, isUndefined } from "../../../../lib/html5-software-library/src/ranj/util/TypeUtil";
import Config from "../config/Config";
import LanguagePackLoadedEvent from "../language/controller/LanguagePackLoadedEvent";
import LanguageModel from "../language/model/LanguageModel";
import AssetLibrary from "./AssetLibrary";
import AssetUrlHelper from "./AssetUrlHelper";
import AssetLoadServiceEvent from "./events/AssetLoadServiceEvent";
import ZipLoadQueue from './ZipLoadQueue';

export default class AssetLoadService {
    /**
     * 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {Array<object>} assetConfigurationManifest 
     * @param {AssetLibrary} assetLibrary 
     * @param {LanguageModel} languageModel 
     * @param {Config} config
     */
    constructor(dispatcher, assetConfigurationManifest, assetLibrary, languageModel, config) {
        this._dispatcher = dispatcher;
        this._assetConfigurationManifest = assetConfigurationManifest;
        this._assetLibrary = assetLibrary;
        this._languageModel = languageModel;
        this._config = config;

        this._version = undefined; // Will be set in the '_onAssetsConfigurationLoaded' method once the config.json file is parsed

        this._loader = new ZipLoadQueue(true);
        this._loader.setMaxConnections(5);
        this._loader.installPlugin(createjs.Sound);

        this._assetsConfigurationLoaded = false;
        this._assetsConfigurationLoading = false;
        this._assetsConfiguration = {};
        //strings are groups, arrays are manifests
        this._itemsToLoad = new Queue();
        // An array to keep all the groups that are currently loading
        // used to mark them as loaded on a complete
        this._itemsLoading = [];

        /** @type {Map<string, {id: string, src: string, bundle: string, ext: string, bundle: string}>} */
        this._itemsThatFailedToLoad = new Map();

        console.info("Loader:", this._loader);

        this._lazyLoadingImages = {};
    }

    getManifest(id) {
        return this._assetsConfiguration[id];
    }

    getProgress() {
        return this._loader.progress;
    }

    loadAssets(manifest) {
        if (this._assetsConfigurationLoaded) {
            this._loader.loadManifest(manifest, true);
        } else {
            this._itemsToLoad.enqueue(manifest);

            //call loading the configuration, it will automatically load the enqueued items
            this._loadAssetsConfiguration();
        }
    }

    loadGroups() {
        if (this._assetsConfigurationLoaded) {
            let nothingToLoad = true;
            //Enqueue all the groups
            for (let i = 0; i < arguments.length; i++) {
                let group = arguments[i];
                let manifest = null;
                //Check if assets are a flash exported library file, or part of the assets configuration:
                if (isUndefined(lib[group]) || isUndefined(lib[group].properties) || isUndefined(lib[group].properties.manifest)) {
                    //Assets should be in the assets configuration
                    if (isUndefined(this._assetsConfiguration[group])) {
                        throw new Error("Asset group '" + group + "' was not found in configuration");
                    }

                    if (!this._assetsConfiguration[group].loaded) {
                        manifest = this._assetsConfiguration[group];
                    } else {
                        console.info("Asset group '" + group + "' is already loaded.");
                    }
                }
                else {
                    //Assets are in a flash exported file
                    //insert spritesheet metadata on spritesheet images to load
                    this._addSpritesheetMetadataToManifest(lib[group]);
                    this._assetsConfiguration[group] = lib[group].properties.manifest;
                    manifest = this._assetsConfiguration[group];
                }
                //Empty manifest will cause load error "PRELOAD_MANIFEST_EMPTY", so skip if empty
                if (manifest != null && manifest.length) {
                    const localizedManifest = this._resolvePathsInManifest(manifest);
                    let mappedManifest = this._cacheBustManifest(localizedManifest); // Append version number behind each source enable cache busting
                    this._loader.loadManifest(mappedManifest, true);
                    this._itemsLoading.push(group);
                    nothingToLoad = false;
                } else {
                    console.info("Asset group '" + group + "' has no assets to load.");
                }
            }
            //Check if there is nothing to load
            if (nothingToLoad && this._itemsToLoad.isEmpty() && this._itemsLoading.length == 0) {
                //Call complete to dispatch an event
                this._onLoadComplete();
            }
        } else {
            //Asset Configuration not yet loaded, so load it first
            //  Enqueue all the groups that are not in the queue 
            for (let i = 0; i < arguments.length; i++) {
                if (!this._itemsToLoad.contains(arguments[i])) {
                    this._itemsToLoad.enqueue(arguments[i]);
                }
            }
            //Call loading the configuration, after which the enqueued groups will be automatically loaded
            this._loadAssetsConfiguration();
        }
    }

    loadGroup(group) {
        this.loadGroups(group);
    }

    retryLoadingItemsThatPreviouslyFailedToLoad() {
        // Empty manifest causes a 'PRELOAD_MANIFEST_EMPTY' error, prevent this error by checking if array is filled
        if (!this._itemsThatFailedToLoad || this._itemsThatFailedToLoad.size === 0) {
            // Dispatch the complete callback as we seem to have finished loading
            this._onLoadComplete();
            return;
        }

        // If zip loading is enabled check if failed items have a 'bundle' property.
        // These bundles have failed loading, so we have to reload them.
        // For this we have to clear/ reset data created for this bundle in the load queue and its zipmanager.
        if (this._config.getValue('settings.zipLoadingEnabled')) {
            const bundlesWithErrors = [...this._itemsThatFailedToLoad.values()] // convert map to array
                .filter(i => i.bundle)
                .map(i => i.bundle);
            const distinctBundlesWithErrors = [...new Set(bundlesWithErrors)];
            for (let i = 0; i < distinctBundlesWithErrors.length; i++) {
                const bundleId = distinctBundlesWithErrors[i];

                this._loader.zipManager.removeZip(bundleId); // remove from zip manager to force a reload of the zip (bundle)
                delete this._loader.bundleData[bundleId]; // remove bundle specific data from loader to mark it as a not yet loaded bundle
            }
        }

        console.log('Retrying to load items that previously failed loading...', this._itemsThatFailedToLoad);
        this.loadAssets([...this._itemsThatFailedToLoad.values()]);
    }

    _loadAllQueuedItems() {
        let item;
        while (!this._itemsToLoad.isEmpty()) {
            item = this._itemsToLoad.dequeue();
            if (isArray(item)) {
                this.loadAssets(item);
            } else if (isString(item)) {
                this.loadGroup(item);
            } else if (item) {
                this.loadAssets([item]);
            }
        }
    }

    _loadAssetsConfiguration() {
        if (this._assetsConfigurationLoaded || this._assetsConfigurationLoading) {
            return;
        }

        this._assetsConfigurationLoading = true;

        this._loader.addEventListener("complete", this._onAssetsConfigurationLoaded.bind(this));
        this._loader.addEventListener("fileload", this._onLoadFileComplete.bind(this));
        this._loader.loadManifest(this._assetConfigurationManifest);
    }

    _onAssetsConfigurationLoaded() {
        this._assetsConfigurationLoaded = true;
        this._assetsConfigurationLoading = false;

        // Remember the version of the build for cache busting.
        // The NGTConfig class is or may be not populated yet at this point
        // This means that a simple 'this._config.getValue('general.build')' won't work,
        // so we take the raw JSON from config.json
        this._version = this._assetLibrary.getAsset("config").general.build;

        //TODO: give a clear error message for parse errors in the json files

        this._addAssetsConfiguration(this._assetLibrary.getAsset("assets"));

        console.info("Assets configuration loaded: ", this._assetsConfiguration);

        //Initialize zip loading if enabled in config
        const ziploadingEnabled = this._assetLibrary.getAsset("config").settings.zipLoadingEnabled;
        console.info("Zip loading enabled: " + ziploadingEnabled);
        if (ziploadingEnabled) {
            this._initializeZipLoading();
        }

        //Setup all 'normal' event listeners
        this._loader.removeAllEventListeners();
        this._loader.addEventListener("complete", this._onLoadComplete.bind(this));
        this._loader.addEventListener("fileload", this._onLoadFileComplete.bind(this));
        this._loader.addEventListener("error", this._onLoadError.bind(this));
        this._loader.addEventListener("progress", this._onLoadProgress.bind(this));
        this._dispatcher.addEventListener(AssetLoadServiceEvent.RETRY_LOADING_FAILED_ITEMS, this.retryLoadingItemsThatPreviouslyFailedToLoad.bind(this));

        this._loadAllQueuedItems();
    }

    _addAssetsConfiguration(assetConfiguration) {
        if (isUndefined(assetConfiguration)) {
            return;
        }

        //Merge the groups from the configuration into the main configuration
        for (let group in assetConfiguration) {
            const existingGroup = this._assetsConfiguration[group];
            if (isUndefined(existingGroup)) {
                this._assetsConfiguration[group] = assetConfiguration[group];
            } else {
                for (let i = 0; i < assetConfiguration[group].length; i++) {
                    existingGroup.push(assetConfiguration[group][i]);
                }
            }
        }
    };

    _onLoadFileComplete(event) {
        const item = event.item;

        // Add to the game's asset library
        this._assetLibrary.addAsset(item.id, event.result);

        if (item.type === createjs.Types.IMAGE) {
            // Exported FLA libraries require the images to be in the
            // "images" global scope object.
            window.images[item.id] = event.result;
            // If spritesheetMetadata was set on object during loadGroup call, apply it
            this._applyImageSpritesheetMetadata(item, event.result);
        }
        else if (item.ext === "lp") {
            const evt = new LanguagePackLoadedEvent(item.id, item.src, event.result);
            this._dispatcher.dispatch(LanguagePackLoadedEvent.LOADED, { data: evt });
        }
        else if (item.ext === "dlg") {
            const result = JSON.parse(event.result);
            const lp = result.languagePack;
            let assetId = item.id + ".lp";
            this._assetLibrary.addAsset(assetId, lp);
            const evt = new LanguagePackLoadedEvent(assetId, item.src, lp);
            this._dispatcher.dispatch(LanguagePackLoadedEvent.LOADED, { data: evt });
        }

        // Call attached onLoadCallback (mainly used for lazy loaded images)
        const onLoadCallbacks = item.onLoadCallbacks;
        if (onLoadCallbacks) {
            for (const onLoadCallback of onLoadCallbacks) {
                onLoadCallback.call(this, item.id);
            }
        }

        // This file might have failed to load previously. If so, remove it from the 'items that failed to load' array.
        const itemDidExist = this._itemsThatFailedToLoad.delete(item.id);
        if (itemDidExist) {
            console.log(`Successfully loaded a file that failed to load previously.`);

            // The loader might fire a complete event before having loaded all previously failed items. That is why we check again here.
            if (this._itemsThatFailedToLoad.size === 0) {
                this._onLoadComplete();
            }
        }
    }

    _onLoadComplete(evt) {
        for (const item of this._itemsLoading) {
            if (isString(item)) {
                this._assetsConfiguration[item].loaded = true;
            }
        }
        this._itemsLoading = [];

        if (this._itemsThatFailedToLoad.size > 0) {
            const evt = new AssetLoadServiceEvent(AssetLoadServiceEvent.LOAD_COMPLETE_WITH_ERRORS, {
                files: this._itemsThatFailedToLoad,
                reloadPageOnRetry: this._config._data.hasOwnProperty('settings'), // We need to access the settings to see whether zip loading is enabled later. Otherwise we cannot correctly handle asset reloading.
            });
            this._dispatcher.dispatch(evt.type, { data: evt });
        } else {
            console.info("Load complete.");
            this._dispatcher.dispatch(AssetLoadServiceEvent.LOAD_COMPLETE);
        }
    }

    _onLoadProgress(event) {
        // createjs.LoadQueue can sometimes report an "ugly" floating point (e.g. 1.0000000009).
        // This will throw an exception in the TaskExecutor, because it expects a number from 0 to 1.
        // That's why we max out the progress to '1' here.
        let progress = Math.min(event.progress, 1);
        if (isNaN(progress)) {
            // can occur when assets failed to load
            return;
        }
        this._dispatcher.dispatch(AssetLoadServiceEvent.LOAD_PROGRESS, { progress });
    }

    _onLoadError(evt) {
        const errorTitle = evt.title;
        const data = evt.data;

        if (!data) {
            console.error(`${errorTitle}`, evt);
            return;
        }

        console.error(`${errorTitle} - Tried loading asset '${data.id}' at '${data.src}' but failed.`, evt);
        this._dispatcher.dispatch(AssetLoadServiceEvent.LOAD_FILE_ERROR);

        if (!this._itemsThatFailedToLoad.has(data.id)) {
            this._itemsThatFailedToLoad.set(data.id, data);
        }
    }

    _cacheBustManifest(manifest) {
        // Append the version of the game behind each source to enable cache busting 
        const postfix = "v=" + this._version;
        manifest.forEach(function (item) {
            const sep = item.src.indexOf("?") > -1 ? "&" : "?"
            item.src += sep + postfix;
        });
        return manifest;
    }

    _resolvePathsInManifest(manifest) {
        return manifest.map(e => {
            e.src = AssetUrlHelper.resolvePath(e.src, this._languageModel.currentLanguage);
            return e;
        });
    }

    /// ----
    /// Lazy loading
    /// ----

    lazyLoadImage(id, url, onLoadCallback, group) {
        group = isNull(group) ? "_default_" : group;

        // check if image is already loaded into the asset library
        if (this._assetLibrary.hasAsset(id)) {
            if (!isNull(onLoadCallback)) {
                onLoadCallback(id);
            }
        } else {
            // check if there is already a group
            let lazyLoadingGroup = this._lazyLoadingImages[group];
            if (isNull(lazyLoadingGroup)) {
                lazyLoadingGroup = this._lazyLoadingImages[group] = {};
            }

            // check if this image is currently under lazy loading, if so, the 'onLoadCallback' will be added to the 'onLoadCallbacks' array
            // if not, add this lazy loading image to the cache object
            // -- this properly handles concurrent lazy loading            
            let lazyLoadedLoadItem = lazyLoadingGroup[id];
            if (isNull(lazyLoadedLoadItem)) {
                lazyLoadedLoadItem = {
                    id,
                    src: url,
                    group,
                };

                lazyLoadingGroup[id] = lazyLoadedLoadItem;
            }

            if (!lazyLoadedLoadItem.onLoadCallbacks) {
                lazyLoadedLoadItem.onLoadCallbacks = [];
            }

            lazyLoadedLoadItem.onLoadCallbacks.push(() => {
                // clean up cached lazy loading
                lazyLoadingGroup[id] = null;
                delete lazyLoadingGroup[id];

                if (onLoadCallback) {
                    onLoadCallback.call(this, id);
                }
            });

            this._itemsToLoad.enqueue(lazyLoadedLoadItem);

            this._loadAllQueuedItems();
        }
    }

    cancelLazyLoadingCallbackForImage(imageID, group) {
        group = isNull(group) ? "_default_" : group;
        const lazyLoadingGroup = this._lazyLoadingImages[group];
        // set callback to lazy loading image to 'null'
        if (!isNull(lazyLoadingGroup) && !isNull(lazyLoadingGroup[imageID])) {
            lazyLoadingGroup[imageID].onLoadCallbacks = null;
            lazyLoadingGroup[imageID] = null;
            delete lazyLoadingGroup[imageID];
        }

        this._stopItemFromBeingLoaded(imageID);
    }

    cancelLazyLoadingCallbacksForGroup(group) {
        group = isNull(group) ? "_default_" : group;
        const lazyLoadingGroup = this._lazyLoadingImages[group];
        if (!isNull(lazyLoadingGroup)) {
            // sets all callbacks to lazy loading images from this group to 'null'
            for (let assetId in lazyLoadingGroup) {
                const lazyLoadingItem = lazyLoadingGroup[assetId];

                this._stopItemFromBeingLoaded(assetId);

                delete lazyLoadingItem.onLoadCallbacks;
            }
            // remove group
            delete this._lazyLoadingImages[group];
        }
    }

    _stopItemFromBeingLoaded(id) {
        // Stop this item from being loaded
        // Check if id is set as passing nothing in the '.remove()' method will cancel loading and remove all items from the load queue
        if (id !== undefined && id !== undefined) {
            this._loader.remove(id);
        }

        // Prevent it from being reloaded if it failed before
        this._itemsThatFailedToLoad.delete(id);
    }


    // Spritesheet methods

    /**
     * Add spritesheet metadata to items to load, where applicable.
     * This is done so after loading the item, the spritesheet can be instantiated based on the metadata.
     * Also see AssetLoadService._applyImageSpritesheetMetadata
     * To use spritesheets, enable spritesheet export in the asset fla's publish settings.
     * @param Object group Exported asset library.
     * @returns {undefined}
     */
    _addSpritesheetMetadataToManifest(group) {
        let manifest = group.properties.manifest;
        let ssMetadata = group.ssMetadata;
        //go through manifest and add spritesheet meta data to spritesheet image entries
        if (ssMetadata != null && ssMetadata.length) {
            let ssMetadataById = {};
            for (let i = 0; i < ssMetadata.length; i++) {
                ssMetadataById[ssMetadata[i].name] = ssMetadata[i];
            }
            for (let i = 0; i < manifest.length; i++) {
                let entry = manifest[i];
                if (ssMetadataById[entry.id] != null) {
                    entry.spritesheetMetadata = ssMetadataById[entry.id];
                }
            }
        }
    }

    /**
     * Check if loaded item has spritesheetMetadata 
     *  If so, instantiate a spritesheet based on the metadata
     * Also see AssetLoadService._addSpritesheetMetadataToManifest.
     * @param LoadItem item Object with the item properties for the image that was loaded.
     * @param HTMLImage image The image data that was loaded
     * @returns {undefined}
     */
    _applyImageSpritesheetMetadata(item, image) {
        //If spritesheetMetadata was set on object during loadGroup call, 
        //  create spritesheet and add to "ss" global scope object.
        //Should be called after loading the image item, so window.images contains the image
        let ssMeta = item.spritesheetMetadata;
        if (ssMeta != null) {
            window.ss[ssMeta.name] = new createjs.SpriteSheet({ "images": [image], "frames": ssMeta.frames })
        }
    }

    _initializeZipLoading() {
        const ziploadingFolder = this._assetLibrary.getAsset("config").settings.zipLoadingFolder;
        this._loader.initializeZipLoading(ziploadingFolder);
    }
}